import {toPurchaseLimitFormValues} from "../../../../components/offering-form/general/settings/purchase-limit-payload";
import {GetStoreServiceApiResponse} from "../../../../react-query-hooks/store/services/get-store-service/types";
import {EditStoreServiceBasicFormStepFormValues} from "../schema-validation";

/**
 * The saved product service in the edit page's form encoding.
 *
 * The update is a full-replace PUT: a field left out of it is reset, not kept. So a tab
 * that edits one part of the service, such as the checkout questions, still has to send
 * everything else exactly as saved. The form tab uses this as its starting values and
 * the checkout tab submits it with its questions on top, so the two cannot drift.
 *
 * Cover and portfolio images are left out: the form tab loads them separately.
 */
export function toSavedEditStoreServiceFormValues(
  storeServiceData: GetStoreServiceApiResponse,
  isPwywEnabled: boolean
): Omit<EditStoreServiceBasicFormStepFormValues, "cover_images" | "portfolio_images"> {
  const {productService} = storeServiceData;
  const savedRedirectType =
    productService.redirectType ?? (productService.url_redirect ? "url" : "none");

  return {
    currencies:
      productService.currencies.length < 1
        ? []
        : productService.currencies
            .filter((c) => !c.is_auto_converted)
            .sort((a, b) => {
              // Put base currency first
              if (a.is_base) return -1;
              if (b.is_base) return 1;
              return 0;
            })
            .map((c) => ({
              callPrice: c.amount,
              callPriceCurrency: c.currency,
              minCallPrice: c.min_call_price,
            })),
    enable_currency_conversion: true,
    selected_currencies: productService.currencies
      .filter((c) => c.is_auto_converted)
      .map((c) => c.currency.toUpperCase()),
    description: productService.description,
    hideFromProfile: productService.hideFromProfile,
    isUrlRedirect: !!productService.url_redirect,
    redirectAfterPurchase: savedRedirectType === "url" ? "custom-url" : savedRedirectType,
    telegramGroupId: productService.telegramGroupChatId ?? undefined,
    name: productService.name,
    priceType:
      productService.currencies.length < 1
        ? "free"
        : isPwywEnabled && productService.is_pay_what_you_want
        ? "pwyw"
        : "paid",
    requestCustomerPhoneNumber: productService.request_phone_number,
    ...toPurchaseLimitFormValues(productService.purchaseLimit),
    url_redirect: productService.url_redirect,
    deliveryTimeFrameUnit: productService.delivery_timeframe_unit,
    deliveryTimeFrameValue: productService.delivery_timeframe_value,
  };
}
